import React, { useEffect, useRef } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, Animated } from 'react-native';
import { MaterialIcons, FontAwesome5 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { normalize } from '../../utils/normalize';
import { PLACEHOLDER_PROFILE_URL } from '../../constants/placeholderImage';

const SkeletonLoader = () => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const shimmer = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 1500,
          useNativeDriver: true,
        }),
      ])
    );
    shimmer.start();
    return () => shimmer.stop();
  }, [shimmerAnim]);

  const opacity = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 0.7],
  });

  return (
    <>
      <View style={styles.avatarContainer}>
        <Animated.View style={[styles.avatar, styles.skeletonAvatar, { opacity }]} />
      </View>
      <View style={styles.infoContainer}>
        <Animated.View style={[styles.skeletonName, { opacity }]} />
        <Animated.View style={[styles.skeletonProfession, { opacity }]} />
      </View>
      <Animated.View style={[styles.skeletonChevron, { opacity }]} />
    </>
  );
};

const UserProfileCard = ({
  name,
  profession,
  isVerified = false,
  profileImage,
  onPress,
  showCompleteProfile = false,
  onCompleteProfilePress,
  isLoading = false
}) => {
  return (
    <View style={styles.gradientContainer}>
      <LinearGradient
        colors={["#ffffff", "#fff"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          borderRadius: 24,
        }}
      >
        {isLoading ? (
          <View style={styles.container}>
            <SkeletonLoader />
          </View>
        ) : (
          <>
            <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
              <View style={styles.container}>
                <View style={styles.avatarContainer}>
                  <Image
                    source={{ uri: profileImage || PLACEHOLDER_PROFILE_URL }}
                    style={styles.avatar}
                  />
                </View>

                <View style={styles.infoContainer}>
                  <View style={styles.nameContainer}>
                    <Text style={styles.name} numberOfLines={1}>{name?.split("-").join(" ")}</Text>
                    {isVerified && (
                      <View style={styles.verifiedBadge}>
                        <FontAwesome5 name="check" size={8} color="#10B981" />
                      </View>
                    )}
                  </View>
                  <Text style={styles.profession} numberOfLines={1}>{profession}</Text>
                </View>

                <MaterialIcons name="chevron-right" size={24} color="rgba(0, 0, 0, 0.6)" style={styles.chevron} />
              </View>
            </TouchableOpacity>

            {showCompleteProfile && (
              <TouchableOpacity
                style={styles.completeProfileButton}
                onPress={onCompleteProfilePress}
                activeOpacity={0.8}
              >
                <Text style={styles.completeProfileText}>Incomplete Profile</Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  gradientContainer: {
    borderRadius: 24,
    marginHorizontal: 12,
    marginTop: 12,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 5,
    elevation: 5,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  avatarContainer: {
    marginRight: 12,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 28,
    borderWidth: 2,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  placeholderAvatar: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    fontSize: normalize(24),
    color: 'rgba(0, 0, 0, 0.7)',
    fontWeight: '600',
  },
  infoContainer: {
    flex: 1,
  },
  nameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  name: {
    fontSize: normalize(20),
    fontWeight: '700',
    color: '#333333',
    marginRight: 8,
  },
  verifiedBadge: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 4,
  },
  profession: {
    fontSize: normalize(14),
    color: 'rgba(0, 0, 0, 0.6)',
  },
  chevron: {
    marginLeft: 8,
  },
  completeProfileButton: {
    backgroundColor: '#f1b739',
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginHorizontal: 20,
    marginTop: 0,
    marginBottom: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.0)',
  },
  completeProfileText: {
    color: '#8e4800',
    fontSize: normalize(12),
    fontWeight: '600',
  },
  skeletonAvatar: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  skeletonName: {
    width: 120,
    height: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 4,
    marginBottom: 8,
  },
  skeletonProfession: {
    width: 80,
    height: 14,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 4,
  },
  skeletonChevron: {
    width: 24,
    height: 24,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 4,
    marginLeft: 8,
  },
});

export default UserProfileCard;